/**
 * API Client
 * Handles communication with the PHP backend
 */

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

interface ApiResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  errors?: Record<string, string>;
  timestamp: number;
}

interface Admin {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'super_admin';
  is_active: boolean;
  last_login_at?: string;
  last_login_ip?: string;
  created_at: string;
}

interface LoginResponse {
  admin: Admin;
  token: string;
}

/**
 * Generic API request handler
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<ApiResponse<T>> {
  const url = `${API_BASE_URL}${endpoint}`;
  
  console.log('API Request:', { url, method: options.method, endpoint });
  
  const headers: HeadersInit = {
    'Accept': 'application/json',
    ...options.headers,
  };
  
  // Only set Content-Type if not FormData (browser will set it with boundary)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  
  // Add authorization token if available (except for payment endpoints)
  const token = localStorage.getItem('revlinks_admin_token');
  if (token && !endpoint.includes('payments.php')) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include', // Include cookies for session-based auth
      mode: 'cors', // Explicitly set CORS mode
    });
    
    console.log('API Response:', { status: response.status, ok: response.ok });
    
    const data = await response.json();
    
    if (!response.ok) {
      const errorMessage = data.message || 'API request failed';
      const error = new Error(errorMessage) as any;
      error.status = response.status;
      error.data = data;
      throw error;
    }
    
    return data;
  } catch (error) {
    console.error('API Error:', error);
    console.error('Failed URL:', url);
    throw error;
  }
}

/**
 * Get cookie value
 */
function getCookie(name: string): string | null {
  if (typeof window === 'undefined') return null;
  
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  
  if (parts.length === 2) {
    return parts.pop()?.split(';').shift() || null;
  }
  
  return null;
}

/**
 * Set cookie value
 */
function setCookie(name: string, value: string, days: number = 1) {
  if (typeof window === 'undefined') return;
  
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Strict`;
}

/**
 * Delete cookie
 */
function deleteCookie(name: string) {
  if (typeof window === 'undefined') return;
  
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;SameSite=Strict`;
}

/**
 * Admin login
 */
export async function adminLogin(email: string, password: string): Promise<LoginResponse> {
  const response = await apiRequest<LoginResponse>('/?action=login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  
  if (response.success && response.data) {
    // Store admin session in localStorage ONLY
    localStorage.setItem('revlinks_admin_id', response.data.admin.id.toString());
    localStorage.setItem('revlinks_admin_email', response.data.admin.email);
    localStorage.setItem('revlinks_admin_name', response.data.admin.name);
    localStorage.setItem('revlinks_admin_role', response.data.admin.role);
    localStorage.setItem('revlinks_admin_token', response.data.token);
  }
  
  return response.data;
}

/**
 * Admin logout
 */
export async function adminLogout(): Promise<void> {
  try {
    await apiRequest('/?action=logout', {
      method: 'POST',
    });
  } catch (error) {
    console.error('Logout error:', error);
  } finally {
    // Clear local storage
    localStorage.removeItem('revlinks_admin_id');
    localStorage.removeItem('revlinks_admin_email');
    localStorage.removeItem('revlinks_admin_name');
    localStorage.removeItem('revlinks_admin_role');
    localStorage.removeItem('revlinks_admin_token');
  }
}

/**
 * Get current admin user
 */
export async function getCurrentAdmin(): Promise<Admin | null> {
  try {
    const response = await apiRequest<Admin>('/?action=me');
    
    if (response.success && response.data) {
      return response.data;
    }
    
    return null;
  } catch (error) {
    console.error('Get current admin error:', error);
    return null;
  }
}

/**
 * Verify authentication
 */
export async function verifyAuth(): Promise<boolean> {
  try {
    const response = await apiRequest<{ valid: boolean; admin: Admin }>('/?action=verify');
    return response.success && response.data?.valid || false;
  } catch (error) {
    console.error('Verify auth error:', error);
    return false;
  }
}

/**
 * Check if admin is logged in (client-side check)
 */
export function adminLoggedIn(): boolean {
  if (typeof window === 'undefined') return false;
  return !!localStorage.getItem('revlinks_admin_token');
}

/**
 * Get current admin from local storage (client-side)
 */
export function getSessionAdmin(): { id: string; email: string; name: string; role: string } | null {
  if (typeof window === 'undefined') return null;
  
  const id = localStorage.getItem('revlinks_admin_id');
  const email = localStorage.getItem('revlinks_admin_email');
  const name = localStorage.getItem('revlinks_admin_name');
  const role = localStorage.getItem('revlinks_admin_role');
  
  if (id && email && name && role) {
    return { id, email, name, role };
  }
  
  return null;
}

/**
 * Logout (client-side only)
 */
export function clientSideLogout() {
  if (typeof window === 'undefined') return;
  
  localStorage.removeItem('revlinks_admin_id');
  localStorage.removeItem('revlinks_admin_email');
  localStorage.removeItem('revlinks_admin_name');
  localStorage.removeItem('revlinks_admin_role');
  localStorage.removeItem('revlinks_admin_token');
}

/**
 * Application API functions
 */

export interface Application {
  id: number;
  application_number?: string;
  full_name: string;
  email: string;
  phone: string;
  age_range: string;
  location: string;
  track: string;
  background?: string;
  document_path?: string;
  status: 'pending' | 'reviewed' | 'code_sent' | 'registered';
  payment_status?: 'unpaid' | 'paid';
  submitted_at: string;
  registered_at?: string;
}

/**
 * Submit application
 */
export async function submitApplication(formData: FormData): Promise<{ id: number; message: string }> {
  try {
    const response = await apiRequest<{ id: number; message: string }>('/?action=create', {
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type for FormData - let browser set it with boundary
      }
    });
    
    if (response.success && response.data) {
      return response.data;
    }
    
    throw new Error(response.message || 'Failed to submit application');
  } catch (error: any) {
    // Preserve the error message from the API response
    if (error.message) {
      throw error;
    }
    throw new Error('Failed to submit application. Please try again.');
  }
}

/**
 * Get all applications (admin only)
 */
export async function getApplications(status?: string): Promise<Application[]> {
  // Map frontend status to database status
  const statusMap: Record<string, string> = {
    'pending': 'pending_review',
    'accepted': 'approved',
    'rejected': 'rejected',
    'code_sent': 'code_sent',
    'code_verified': 'payment_pending',
    'registered': 'enrolled'
  };
  
  const dbStatus = status ? (statusMap[status] || status) : undefined;
  const url = dbStatus ? `/?action=list&status=${dbStatus}` : '/?action=list';
  const response = await apiRequest<Application[]>(url);
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.message || 'Failed to get applications');
}

/**
 * Get application by ID (admin only)
 */
export async function getApplication(id: number): Promise<Application> {
  const response = await apiRequest<Application>(`/?action=get&id=${id}`);
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.message || 'Failed to get application');
}

/**
 * Update application status (admin only)
 */
export async function updateApplicationStatus(id: number, status: string): Promise<void> {
  // Map frontend status to database status
  const statusMap: Record<string, string> = {
    'pending': 'pending_review',
    'reviewed': 'under_review',
    'code_sent': 'code_sent',
    'registered': 'registered'
  };
  
  const dbStatus = statusMap[status] || status;
  
  const response = await apiRequest(`/?action=update_status&id=${id}&status=${dbStatus}`, {
    method: 'POST',
  });
  
  if (!response.success) {
    throw new Error(response.message || 'Failed to update status');
  }
}

/**
 * Set registration code (admin only)
 */
export async function setRegistrationCode(id: number, code: string): Promise<string> {
  const response = await apiRequest<{ code: string }>(`/?action=set_code&id=${id}`, {
    method: 'POST',
    body: JSON.stringify({ code }),
  });
  
  if (!response.success) {
    throw new Error(response.message || 'Failed to set registration code');
  }
  
  // Return the code that was set (either the one we sent or the one generated by server)
  return response.data?.code || code;
}

/**
 * Payment API functions
 */

export interface PaymentData {
  email: string;
  amount: number;
  application_id?: number;
}

export interface PaystackResponse {
  status: boolean;
  message: string;
  data: {
    reference: string;
    authorization_url: string;
    access_code: string;
  };
}

/**
 * Initialize payment
 */
export async function initializePayment(data: PaymentData): Promise<PaystackResponse> {
  // Paystack requires different authorization - handle separately
  const response = await apiRequest<PaystackResponse>('/?action=initialize', {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      // Don't send admin auth token to our backend for payment
      'X-Skip-Auth': 'true'
    }
  });
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.message || 'Failed to initialize payment');
}

/**
 * Verify payment
 */
export async function verifyPayment(reference: string): Promise<any> {
  const response = await apiRequest(`/?action=verify&reference=${reference}`);
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.message || 'Failed to verify payment');
}

/**
 * Delete application (admin only)
 */
export async function deleteApplication(id: number): Promise<void> {
  const response = await apiRequest(`/?action=delete&id=${id}`, {
    method: 'DELETE',
  });
  
  if (!response.success) {
    throw new Error(response.message || 'Failed to delete application');
  }
}

/**
 * Search applications (admin only)
 */
export async function searchApplications(query: string): Promise<Application[]> {
  const response = await apiRequest<Application[]>(`/?action=search&q=${encodeURIComponent(query)}`);
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.message || 'Failed to search applications');
}

/**
 * Get application statistics (admin only)
 */
export async function getApplicationStatistics(): Promise<{
  total: number;
  pending: number;
  reviewed: number;
  code_sent: number;
  registered: number;
}> {
  const response = await apiRequest('/?action=statistics');
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.message || 'Failed to get statistics');
}

/**
 * Waitlist API functions
 */

export interface WaitlistEntry {
  id: number;
  full_name: string;
  email: string;
  phone: string;
  location: string;
  additional_information: string;
  status: string;
  created_at: string;
  updated_at: string;
}

/**
 * Get all waitlist entries (admin only)
 */
export async function getWaitlistEntries(): Promise<WaitlistEntry[]> {
  const response = await apiRequest<WaitlistEntry[]>('/?action=waitlist_list');
  
  if (response.success && response.data) {
    return response.data;
  }
  
  throw new Error(response.message || 'Failed to get waitlist entries');
}
